import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { SUPPRESS_ERROR_TOAST } from '../core/api/api-context';
import { ErrorHelpersService } from '../helpers/error-helpers.service';

/**
 * Global error interceptor — centralizes user-facing error toasts.
 *
 * Sits OUTERMOST in the chain so it observes the final outcome of every request
 * (after the auth interceptor's 401 refresh+retry has run). It shows a friendly,
 * production-safe toast via {@link ErrorHelpersService} and ALWAYS rethrows, so
 * each feature service's own `error:` callback still fires — preserving existing
 * inline error handling.
 *
 * It deliberately stays silent for:
 *  - `401` — owned by the auth interceptor (refresh/retry, then a single
 *    "session expired" toast on hard failure).
 *  - requests with `SUPPRESS_ERROR_TOAST` set — either by a caller handling the
 *    error inline, or by the auth interceptor on a refresh failure (avoids a
 *    duplicate generic toast on top of the session-expired one).
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const errorHelper = inject(ErrorHelpersService);

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      const suppressed = req.context.get(SUPPRESS_ERROR_TOAST) || err.status === 401;
      if (!suppressed) {
        errorHelper.handleHttpsError(err);
      }
      return throwError(() => err);
    }),
  );
};
